import classnames from 'classnames';
import { Fragment, memo, useEffect, useState } from 'react';
import { Chat } from 'stream-chat-react';

import { getAuthAccount, getAuthUser } from '@/shared/auth/selectors';
import { useClickOutside } from '@/shared/hooks/useClickOutside';
import FontIcon from '@/shared/ui/FontIcon';
import { isArray } from '@/shared/util/types';
import { useWiwDispatch, useWiwSelector } from '@/store';
import { track } from '@/workchat/actions';
import { WORKCHAT_VIEWS } from '@/workchat/constants';
import LoadingV2 from '@/workchat/v2/components/LoadingV2';
import { useStreamProvider } from '@/workchat/v2/providers/StreamProvider';
import { getUnreadCountForAllConversations } from '@/workchat/v2/store/conversations/conversationsSelectors';
import { toggleShow } from '@/workchat/v2/store/reducer';
import {
  getWorkchatFlags,
  getWorkchatSelectedConversationId,
  getWorkchatShow,
  getWorkchatView,
} from '@/workchat/v2/store/selectors';
import ConversationListV3 from '@/workchat/v3/components/ConversationListV3/ConversationListV3';
import CreateNewConversationV3 from '@/workchat/v3/components/CreateNewConversation/CreateNewConversation';
import NewChannel from '@/workchat/v3/components/NewChannel/NewChannel';
import NewMessage from '@/workchat/v3/components/NewMessage/NewMessage';
import AddParticipantsV3 from '@/workchat/v3/components/ViewConversationV3/ConversationDetailsV3/AddParticipantsV3/AddParticipantsV3';
import ConversationDetailsV3 from '@/workchat/v3/components/ViewConversationV3/ConversationDetailsV3/ConversationDetailsV3';
import ViewConversationV3 from '@/workchat/v3/components/ViewConversationV3/ViewConversationV3';

import 'stream-chat-react/dist/css/v2/index.css';
import '@/workchat/styles/global.scss';
import '@/workchat/v3/components/WorkChatV3.scss';

const DEFAULT_MAX_UNREAD_COUNT = 9;

export default memo(function WorkChatV3() {
  const dispatch = useWiwDispatch();
  const user = useWiwSelector(getAuthUser);
  const account = useWiwSelector(getAuthAccount);
  const view = useWiwSelector(getWorkchatView);

  const show = useWiwSelector(getWorkchatShow);
  const unread = useWiwSelector(getUnreadCountForAllConversations);
  const flags = useWiwSelector(getWorkchatFlags);
  const selectConversation = useWiwSelector(getWorkchatSelectedConversationId);

  const { client } = useStreamProvider();

  // As per https://mantine.dev/hooks/use-click-outside/#multiple-nodes: will work only with useState, not useRef
  const [workchatContainer, setWorkchatContainer] = useState<HTMLDivElement | null>(null);
  const toastContainer = document.querySelector('.Toastify') as HTMLElement;
  const workchatButton = document.querySelector('#workchat-container > .toggle-dropdown') as HTMLElement;
  const elementsToIgnore = [workchatContainer, toastContainer, workchatButton];

  useClickOutside(() => dispatch(toggleShow(false)), null, elementsToIgnore);

  const enableWorkchat = account.settings.workchat.enabled && user.id > 0;

  useEffect(() => {
    if (!enableWorkchat) {
      return;
    }
  }, []);

  const toggleWorkchat = () => {
    const newShow = !show;
    dispatch(toggleShow(newShow));

    if (newShow) {
      const hasConversations = flags?.get('conversations');
      if (!hasConversations || selectConversation) {
        dispatch(track('WorkChat: Viewed Channel', { accessType: 'Channel List' }));
      } else {
        dispatch(track('WorkChat: Accessed', { accessType: 'Nav Bar' }));
      }
    }
  };

  const renderView = () => {
    let viewName = view;
    let payload: any;

    if (isArray(view)) {
      const [viewNameFromTuple, payloadFromTuple] = view;
      viewName = viewNameFromTuple;
      payload = payloadFromTuple;
    }

    switch (viewName) {
      case WORKCHAT_VIEWS.VIEW_CONVERSATION_LIST: {
        return <ConversationListV3 />;
      }
      case WORKCHAT_VIEWS.VIEW_CONVERSATION: {
        return <ViewConversationV3 />;
      }
      case WORKCHAT_VIEWS.VIEW_CREATE_NEW_CONVERSATION: {
        return <CreateNewConversationV3 />;
      }
      case WORKCHAT_VIEWS.VIEW_NEW_MESSAGE: {
        return <NewMessage />;
      }
      case WORKCHAT_VIEWS.VIEW_NEW_CHANNEL: {
        return <NewChannel preselectedUsers={payload?.selectedUsers} userCache={payload?.userCache} />;
      }
      case WORKCHAT_VIEWS.VIEW_CONVERSATION_DETAIL: {
        return <ConversationDetailsV3 />;
      }
      case WORKCHAT_VIEWS.VIEW_ADD_PARTICIPANTS: {
        return <AddParticipantsV3 channelUserIds={payload?.channelUserIds} />;
      }
      //   case VIEW_RECEIPTS: {
      //     return <ViewReceiptsV2 />;
      //   }
      //   case VIEW_LOADING: {
      //     return <LoadingV2 />;
      //   }
    }
  };

  return (
    <Fragment>
      <button
        type="button"
        aria-expanded={show}
        aria-haspopup="true"
        className={`toggle-dropdown nav-item-button ${show ? 'open' : ''}`}
        onClick={toggleWorkchat}
      >
        <FontIcon icon="workchat" srText={show ? 'Hide WorkChat' : 'Show WorkChat'} />
        {!!unread && (
          <div className="unread-count">
            <div>{unread <= DEFAULT_MAX_UNREAD_COUNT ? unread : `${DEFAULT_MAX_UNREAD_COUNT}+`}</div>
          </div>
        )}
        <span className="nav-item-label hide-label-tablet">WorkChat</span>
      </button>
      <div className={classnames('WorkChatV3 workchat-main', { 'd-none': !show })} ref={setWorkchatContainer}>
        {client ? (
          <Chat client={client} theme="str-chat__theme-dark">
            {renderView()}
          </Chat>
        ) : (
          <LoadingV2 />
        )}
      </div>
    </Fragment>
  );
});
